import { StylesConfig, GroupBase } from 'react-select';
import styled from 'styled-components';

import { DefaultText } from '@components/text';
import { theme } from 'theme';

export const getCustomStyles = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  hasError?: boolean,
): StylesConfig<Option, IsMulti, Group> => ({
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    width: '100%',
  }),
  control: (
    provided: Record<string, unknown>,
    state: { menuIsOpen: boolean },
  ) => ({
    ...provided,
    minHeight: '40px',
    backgroundColor: 'transparent',
    border: `0.5px solid ${
      hasError
        ? theme.colors.red[500]
        : state.menuIsOpen
          ? theme.colors.white
          : theme.colors.grey[300]
    }`,
    boxShadow: 'none',
    '&:hover': {
      border: `0.5px solid ${hasError ? theme.colors.red[500] : theme.colors.grey[300]}`,
    },
    '&:focus': {
      border: `0.5px solid ${hasError ? theme.colors.red[500] : theme.colors.white}`,
    },
    '&:active': {
      border: `0.5px solid ${hasError ? theme.colors.red[500] : theme.colors.white}`,
    },
    '&:hover:not(:focus)': {
      border: `0.5px solid ${hasError ? theme.colors.red[500] : theme.colors.white}`,
    },
  }),
  singleValue: (provided: Record<string, unknown>) => ({
    ...provided,
    color: theme.colors.white,
    fontSize: theme.fonts.fontSize.base,
    fontWeight: 400,
    lineHeight: '24px',
    textAlign: 'left' as const,
  }),
  valueContainer: (provided: Record<string, unknown>) => ({
    ...provided,
    padding: '2px 8px 2px 16px',
  }),
  placeholder: (provided: Record<string, unknown>) => ({
    ...provided,
    color: theme.colors.grey[300],
    fontSize: theme.fonts.fontSize.base,
    fontWeight: 400,
    lineHeight: '24px',
    opacity: 0.7,
    textAlign: 'left' as const,
  }),
  input: (provided: Record<string, unknown>) => ({
    ...provided,
    color: theme.colors.white,
    textAlign: 'left' as const,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: () => ({
    padding: '0 8px',
    color: theme.colors.grey[300],
  }),
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    backgroundColor: theme.colors.grey[700],
    border: `1px solid ${theme.colors.grey[600]}`,
    borderRadius: '4px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    zIndex: 9999,
    right: 0,
    left: 'auto',
  }),
  menuList: (provided: Record<string, unknown>) => ({
    ...provided,
    padding: 0,
    maxHeight: '200px',
    overflowY: 'auto' as const,
  }),
  option: (
    provided: Record<string, unknown>,
    state: { isSelected: boolean; isFocused: boolean },
  ) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? theme.colors.purple[300]
      : 'transparent',
    color: state.isSelected ? theme.colors.grey[700] : theme.colors.grey[200],
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: theme.fonts.fontSize.base,
    fontWeight: 400,
    lineHeight: '24px',
    textAlign: 'left' as const,
    '&:hover': {
      backgroundColor: theme.colors.purple[100],
      color: theme.colors.grey[700],
    },
    '&:active': {
      backgroundColor: theme.colors.purple[200],
      color: theme.colors.grey[700],
    },
  }),
});

export const SelectWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;

  .react-select__menu-list {
    ${({ theme }) => theme.scrollbar.thin}
  }

  .react-select__menu {
    right: 0;
    left: auto;
  }
`;

export const Label = styled(DefaultText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  color: ${({ theme }) => theme.colors.grey[200]};
`;

export const ErrorText = styled.div`
  color: ${({ theme }) => theme.colors.red[500]};
  font-size: ${({ theme }) => theme.fonts.fontSize.xs};
  margin-top: 2px;
  float: left;
`;
